import * as React from 'react';

import BoxSection from '@src/components/BoxSection';
import PageSection from '@src/components/PageSection';
import CardSection from '@src/components/CardSection';
import CardProfile from '@src/components/Cards/CardProfile';
import {graphql} from 'gatsby';

import type {BasicTeamListFragment} from '@graphql-types';

type Document = Extract<
  BasicTeamListFragment['items'][0]['member']['document'],
  {id: string}
>;

interface BasicTeamItemWithDocument {
  member: {
    document: Document;
  };
}

interface TeamListProps {
  slice: BasicTeamListFragment;
}

const TeamList: React.FC<TeamListProps> = ({slice}) => {
  const cards = slice.items
    // Ensure repeatable items have a valid team member link.
    .filter(
      (item): item is BasicTeamItemWithDocument =>
        item.member?.document && 'id' in item.member.document,
    )
    .map(item => {
      const member = item.member.document;
      return (
        <CardProfile
          key={member.id}
          heading={member.data.name.text}
          subheading={member.data.title.text}
          image={member.data.image.url && (member.data.image as Image)}>
          <div>
            {member.data.bio.text && (
              <div dangerouslySetInnerHTML={{__html: member.data.bio.html}} />
            )}
            {member.data.linkedin_link && (
              <a href={member.data.linkedin_link.url}>LinkedIn</a>
            )}
          </div>
        </CardProfile>
      );
    });

  // Don't display this component if there are no team members.
  if (!cards.length) {
    return null;
  }

  return (
    <PageSection>
      <BoxSection theme="beige">
        <CardSection sectionTitle={slice.primary.heading.text}>
          {cards}
        </CardSection>
      </BoxSection>
    </PageSection>
  );
};

export default TeamList;

export const TeamListFragment = graphql`
  fragment basicTeamList on PrismicPageDataBodyTeamList {
    id
    slice_type
    primary {
      heading {
        html
        text
      }
    }
    items {
      member {
        document {
          ... on PrismicTeamMember {
            id
            data {
              name {
                text
              }
              title {
                text
              }
              bio {
                html
                text
              }
              image {
                url
              }
              linkedin_link {
                url
              }
            }
          }
        }
      }
    }
  }
`;
